/**
 * YouTube Helper Utility
 *
 * Functions for working with YouTube URLs and metadata
 *
 * Note: For production, integrate with YouTube Data API v3
 * Get API key from: https://console.developers.google.com/
 * Add to .env: NEXT_PUBLIC_YOUTUBE_API_KEY=your_key_here
 */

export interface YouTubeMetadata {
  id: string
  title: string
  thumbnail: string
  duration: number | null
  channelTitle: string | null
}

/**
 * Extract YouTube video ID from various URL formats
 */
export function extractYouTubeId(url: string): string | null {
  const patterns = [
    // Standard watch URL: youtube.com/watch?v=VIDEO_ID
    /(?:youtube\.com\/watch\?v=)([^&\n?#]+)/,
    // Short URL: youtu.be/VIDEO_ID
    /(?:youtu\.be\/)([^&\n?#]+)/,
    // Shorts: youtube.com/shorts/VIDEO_ID
    /(?:youtube\.com\/shorts\/)([^&\n?#]+)/,
    // Embed: youtube.com/embed/VIDEO_ID
    /(?:youtube\.com\/embed\/)([^&\n?#]+)/,
  ]

  for (const pattern of patterns) {
    const match = url.match(pattern)
    if (match && match[1]) {
      return match[1]
    }
  }

  return null
}

/**
 * Check if a URL is a valid YouTube URL
 */
export function isYouTubeUrl(url: string): boolean {
  return extractYouTubeId(url) !== null
}

/**
 * Get YouTube thumbnail URL from video ID
 */
export function getYouTubeThumbnail(videoId: string, quality: 'default' | 'medium' | 'high' | 'maxres' = 'high'): string {
  const qualityMap = {
    default: 'default.jpg',      // 120x90
    medium: 'mqdefault.jpg',     // 320x180
    high: 'hqdefault.jpg',       // 480x360
    maxres: 'maxresdefault.jpg'  // 1280x720
  }

  return `https://img.youtube.com/vi/${videoId}/${qualityMap[quality]}`
}

/**
 * Fetch YouTube video metadata using YouTube Data API v3
 *
 * For now, returns mock data. In production, integrate with real API:
 * https://developers.google.com/youtube/v3/docs/videos/list
 */
export async function fetchYouTubeMetadata(videoId: string): Promise<YouTubeMetadata | null> {
  try {
    const apiKey = import.meta.env.VITE_YOUTUBE_API_KEY

    if (!apiKey) {
      // No API key - return basic metadata
      console.warn('YouTube API key not configured. Using basic metadata.')
      return {
        id: videoId,
        title: `YouTube Video: ${videoId}`,
        thumbnail: getYouTubeThumbnail(videoId, 'high'),
        duration: null,
        channelTitle: null
      }
    }

    // Fetch from YouTube Data API v3
    const response = await fetch(
      `https://www.googleapis.com/youtube/v3/videos?part=snippet,contentDetails&id=${videoId}&key=${apiKey}`
    )

    if (!response.ok) {
      throw new Error(`YouTube API error: ${response.status}`)
    }

    const data = await response.json()

    if (!data.items || data.items.length === 0) {
      throw new Error('Video not found')
    }

    const video = data.items[0]
    const snippet = video.snippet
    const contentDetails = video.contentDetails

    // Parse ISO 8601 duration (e.g., "PT1H2M10S" = 1 hour, 2 minutes, 10 seconds)
    const duration = parseDuration(contentDetails.duration)

    return {
      id: videoId,
      title: snippet.title,
      thumbnail: snippet.thumbnails?.high?.url || getYouTubeThumbnail(videoId, 'high'),
      duration,
      channelTitle: snippet.channelTitle
    }
  } catch (error) {
    console.error('Error fetching YouTube metadata:', error)
    // Fallback to basic metadata
    return {
      id: videoId,
      title: `YouTube Video: ${videoId}`,
      thumbnail: getYouTubeThumbnail(videoId, 'high'),
      duration: null,
      channelTitle: null
    }
  }
}

/**
 * Parse ISO 8601 duration to seconds
 * Example: "PT1H2M10S" = 3730 seconds
 */
function parseDuration(isoDuration: string): number | null {
  try {
    const match = isoDuration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/)
    if (!match) return null

    const hours = parseInt(match[1] || '0', 10)
    const minutes = parseInt(match[2] || '0', 10)
    const seconds = parseInt(match[3] || '0', 10)

    return hours * 3600 + minutes * 60 + seconds
  } catch (error) {
    console.error('Error parsing duration:', error)
    return null
  }
}

/**
 * Search YouTube videos (using YouTube Data API v3)
 *
 * For now, returns empty array. In production, integrate with real API:
 * https://developers.google.com/youtube/v3/docs/search/list
 */
export async function searchYouTube(query: string, maxResults: number = 10): Promise<YouTubeMetadata[]> {
  try {
    const apiKey = import.meta.env.VITE_YOUTUBE_API_KEY

    if (!apiKey) {
      console.warn('YouTube API key not configured. Search unavailable.')
      return []
    }

    const response = await fetch(
      `https://www.googleapis.com/youtube/v3/search?part=snippet&q=${encodeURIComponent(query)}&type=video&maxResults=${maxResults}&key=${apiKey}`
    )

    if (!response.ok) {
      throw new Error(`YouTube API error: ${response.status}`)
    }

    const data = await response.json()

    if (!data.items) {
      return []
    }

    // Fetch durations for all videos (requires separate API call)
    const videoIds = data.items.map((item: any) => item.id.videoId).join(',')
    const detailsResponse = await fetch(
      `https://www.googleapis.com/youtube/v3/videos?part=contentDetails&id=${videoIds}&key=${apiKey}`
    )

    const detailsData = await detailsResponse.json()
    const durationMap: { [key: string]: number | null } = {}

    if (detailsData.items) {
      detailsData.items.forEach((video: any) => {
        durationMap[video.id] = parseDuration(video.contentDetails.duration)
      })
    }

    return data.items.map((item: any) => ({
      id: item.id.videoId,
      title: item.snippet.title,
      thumbnail: item.snippet.thumbnails?.high?.url || getYouTubeThumbnail(item.id.videoId, 'high'),
      duration: durationMap[item.id.videoId] || null,
      channelTitle: item.snippet.channelTitle
    }))
  } catch (error) {
    console.error('Error searching YouTube:', error)
    return []
  }
}

/**
 * Format duration in seconds to readable string (e.g., "1:23:45")
 */
export function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  return `${minutes}:${secs.toString().padStart(2, '0')}`
}

/**
 * Generate YouTube embed URL
 */
export function getYouTubeEmbedUrl(videoId: string, autoplay: boolean = false): string {
  return `https://www.youtube.com/embed/${videoId}?autoplay=${autoplay ? '1' : '0'}&controls=0&modestbranding=1&rel=0`
}
